import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { useAuth } from '../hooks/useAuth'
import { db } from '../config/firebase'
import { collection, addDoc, deleteDoc, doc, updateDoc, getDocs, where, query } from 'firebase/firestore'
// import { emailjsConfig, initEmailJS } from '../config/emailjs'

// EmailJS configuration - should be set from environment variables
declare global {
  interface Window {
    emailjs: {
      init: (publicKey: string) => void
      send: (serviceId: string, templateId: string, params: any) => Promise<any>
    }
  }
}

interface NotificationSettings {
  enabled: boolean
  medicationReminders: boolean
  stockAlerts: boolean
  adherenceReports: boolean
  reminderTimeMinutes: number // Minutes before scheduled time
  quietHoursStart: string // HH:MM format
  quietHoursEnd: string // HH:MM format
  emailNotifications: boolean
  lowStockEmailAlerts: boolean
  missedDoseEmailAlerts: boolean
}

interface EmailRecipient {
  id: string
  email: string
  lowStockAlerts: boolean
  missedDoseAlerts: boolean
  createdAt?: Date
}

interface NotificationContextType {
  settings: NotificationSettings
  permission: NotificationPermission
  isSupported: boolean
  scheduledRemindersCount: number
  emailRecipients: EmailRecipient[]
  updateSettings: (newSettings: Partial<NotificationSettings>) => Promise<void>
  requestPermission: () => Promise<boolean>
  sendNotification: (title: string, options?: NotificationOptions) => void
  scheduleReminder: (medicationName: string, scheduledTime: string) => void
  cancelReminder: (medicationId: string, scheduledTime: string) => void
  sendStockAlert: (medicationName: string, currentStock: number) => void
  addEmailRecipient: (email: string) => Promise<void>
  removeEmailRecipient: (recipientId: string) => Promise<void>
  updateEmailRecipient: (recipientId: string, updates: Partial<EmailRecipient>) => Promise<void>
  sendTestEmail: (recipientIds: string[]) => Promise<boolean>
  sendLowStockEmail: (medication: any, currentStock: number) => Promise<boolean>
  sendMissedDoseEmail: (medication: any, missedTime: string) => Promise<boolean>
}

const defaultSettings: NotificationSettings = {
  enabled: false,
  medicationReminders: true,
  stockAlerts: true,
  adherenceReports: false,
  reminderTimeMinutes: 15,
  quietHoursStart: '22:00',
  quietHoursEnd: '07:00',
  emailNotifications: false,
  lowStockEmailAlerts: false,
  missedDoseEmailAlerts: false
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined)

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [settings, setSettings] = useState<NotificationSettings>(defaultSettings)
  const [permission, setPermission] = useState<NotificationPermission>('default')
  const [isSupported, setIsSupported] = useState(false)
  const [scheduledReminders, setScheduledReminders] = useState<Map<string, number>>(new Map())
  const [emailRecipients, setEmailRecipients] = useState<EmailRecipient[]>([])
  
  const { user } = useAuth()

  // Check notification support
  useEffect(() => {
    setIsSupported('Notification' in window && 'serviceWorker' in navigator)
    if ('Notification' in window) {
      setPermission(Notification.permission)
    }
  }, [])

  // Load user notification settings
  useEffect(() => {
    if (user?.uid) {
      loadUserSettings()
    }
  }, [user?.uid])

  const loadUserSettings = async () => {
    try {
      const savedSettings = localStorage.getItem(`notification_settings_${user?.uid}`)
      if (savedSettings) {
        const parsed = JSON.parse(savedSettings)
        setSettings({ ...defaultSettings, ...parsed })
      }
    } catch (error) {
      console.error('Error loading notification settings:', error)
    }
  }

  const updateSettings = async (newSettings: Partial<NotificationSettings>) => {
    try {
      const updatedSettings = { ...settings, ...newSettings }
      setSettings(updatedSettings)
      
      if (user?.uid) {
        localStorage.setItem(`notification_settings_${user.uid}`, JSON.stringify(updatedSettings))
      }
      
      // If notifications are being disabled, clear all scheduled reminders
      if (newSettings.enabled === false || newSettings.medicationReminders === false) {
        clearAllReminders()
      }
    } catch (error) {
      console.error('Error updating notification settings:', error)
    }
  }

  const requestPermission = async (): Promise<boolean> => {
    if (!isSupported) {
      console.warn('Notifications not supported')
      return false
    }

    try {
      const result = await Notification.requestPermission()
      setPermission(result)
      
      if (result === 'granted') {
        await updateSettings({ enabled: true })
        return true
      }
      return false
    } catch (error) {
      console.error('Error requesting notification permission:', error)
      return false
    }
  }

  const sendNotification = useCallback((title: string, options?: NotificationOptions) => {
    if (!settings.enabled || permission !== 'granted' || !isSupported) {
      return
    }

    // Check quiet hours
    if (isInQuietHours()) {
      console.log('Notification blocked: quiet hours active')
      return
    }

    const defaultOptions: NotificationOptions = {
      icon: '/icon-192x192.png',
      badge: '/icon-192x192.png',
      tag: 'medication-reminder',
      requireInteraction: true,
      ...options
    }

    try {
      if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
        // Send through service worker for better reliability
        navigator.serviceWorker.controller.postMessage({
          type: 'SHOW_NOTIFICATION',
          title,
          options: defaultOptions
        })
      } else {
        // Fallback to direct notification
        new Notification(title, defaultOptions)
      }
    } catch (error) {
      console.error('Error sending notification:', error)
    }
  }, [settings.enabled, permission, isSupported, settings.quietHoursStart, settings.quietHoursEnd])

  const isInQuietHours = (): boolean => {
    const now = new Date()
    const currentTime = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`
    
    const start = settings.quietHoursStart
    const end = settings.quietHoursEnd
    
    // Handle overnight quiet hours (e.g., 22:00 to 07:00)
    if (start > end) {
      return currentTime >= start || currentTime <= end
    } else {
      return currentTime >= start && currentTime <= end
    }
  }

  const scheduleReminder = useCallback((medicationName: string, scheduledTime: string) => {
    if (!settings.enabled || !settings.medicationReminders || permission !== 'granted') {
      return
    }

    const reminderKey = `${medicationName}-${scheduledTime}`
    
    // Clear existing reminder if any and add new one
    setScheduledReminders(prev => {
      const existingTimer = prev.get(reminderKey)
      if (existingTimer) {
        window.clearTimeout(existingTimer)
      }

      // Calculate time until reminder
      const now = new Date()
      const [hours, minutes] = scheduledTime.split(':').map(Number)
      const scheduledDate = new Date()
      scheduledDate.setHours(hours, minutes, 0, 0)
      
      // If scheduled time is in the past, schedule for tomorrow
      if (scheduledDate <= now) {
        scheduledDate.setDate(scheduledDate.getDate() + 1)
      }
      
      // Schedule reminder X minutes before
      const reminderTime = new Date(scheduledDate.getTime() - (settings.reminderTimeMinutes * 60 * 1000))
      const timeUntilReminder = reminderTime.getTime() - now.getTime()
      
      if (timeUntilReminder > 0) {
        const timerId = window.setTimeout(() => {
          sendNotification(`💊 Recordatorio de Medicación`, {
            body: `Es hora de tomar: ${medicationName}`,
            tag: `medication-${medicationName}`,
            data: { medicationName, scheduledTime }
          })
          
          // Remove from scheduled reminders
          setScheduledReminders(current => {
            const newMap = new Map(current)
            newMap.delete(reminderKey)
            return newMap
          })
        }, timeUntilReminder)
        
        console.log(`Reminder scheduled for ${medicationName} at ${reminderTime.toLocaleTimeString()}`)
        
        // Store the timer ID
        const newMap = new Map(prev)
        newMap.set(reminderKey, timerId)
        return newMap
      }
      
      return prev
    })
  }, [settings, permission, sendNotification])

  const cancelReminder = useCallback((medicationId: string, scheduledTime: string) => {
    const reminderKey = `${medicationId}-${scheduledTime}`
    
    setScheduledReminders(prev => {
      const timerId = prev.get(reminderKey)
      
      if (timerId) {
        window.clearTimeout(timerId)
        console.log(`Reminder cancelled for ${medicationId} at ${scheduledTime}`)
        
        const newMap = new Map(prev)
        newMap.delete(reminderKey)
        return newMap
      }
      
      return prev
    })
  }, [])

  const clearAllReminders = useCallback(() => {
    setScheduledReminders(prev => {
      prev.forEach((timerId) => {
        window.clearTimeout(timerId)
      })
      return new Map()
    })
    console.log('All reminders cleared')
  }, [])

  const sendStockAlert = useCallback((medicationName: string, currentStock: number) => {
    if (!settings.enabled || !settings.stockAlerts || permission !== 'granted') {
      return
    }

    const alertLevel = currentStock === 0 ? 'CRÍTICO' : 'BAJO'
    const emoji = currentStock === 0 ? '🚨' : '⚠️'
    
    sendNotification(`${emoji} Stock ${alertLevel}`, {
      body: currentStock === 0 
        ? `Se agotó: ${medicationName}. ¡Consigue más pronto!`
        : `Stock bajo: ${medicationName} (${currentStock} restantes)`,
      tag: `stock-alert-${medicationName}`,
      data: { medicationName, currentStock, type: 'stock-alert' }
    })
  }, [settings, permission, sendNotification])

  // Email Recipients Management
  const loadEmailRecipients = async () => {
    if (!user?.uid) return
    
    try {
      const recipientsRef = collection(db, 'users', user.uid, 'email-recipients')
      const snapshot = await getDocs(recipientsRef)
      
      const recipients: EmailRecipient[] = snapshot.docs.map(doc => {
        const data = doc.data()
        return {
          id: doc.id,
          email: data.email,
          lowStockAlerts: data.lowStockAlerts ?? true,
          missedDoseAlerts: data.missedDoseAlerts ?? true,
          createdAt: data.createdAt?.toDate() || new Date()
        }
      })
      
      setEmailRecipients(recipients)
      
      // Auto-enable email notifications if recipients exist
      if (recipients.length > 0 && !settings.emailNotifications) {
        await updateSettings({ 
          emailNotifications: true,
          lowStockEmailAlerts: true,
          missedDoseEmailAlerts: true
        })
      }
    } catch (error) {
      console.error('Error loading email recipients:', error)
    }
  }

  const addEmailRecipient = async (email: string) => {
    if (!user?.uid) throw new Error('User not authenticated')
    
    try {
      const recipientsRef = collection(db, 'users', user.uid, 'email-recipients')
      const newRecipientData = {
        email,
        lowStockAlerts: true,
        missedDoseAlerts: true,
        createdAt: new Date()
      }
      
      const docRef = await addDoc(recipientsRef, newRecipientData)
      
      const recipient: EmailRecipient = {
        id: docRef.id,
        ...newRecipientData
      }
      
      setEmailRecipients(prev => [...prev, recipient])
    } catch (error) {
      console.error('Error adding email recipient:', error)
      throw error
    }
  }

  const removeEmailRecipient = async (recipientId: string) => {
    if (!user?.uid) throw new Error('User not authenticated')
    
    try {
      const recipientRef = doc(db, 'users', user.uid, 'email-recipients', recipientId)
      await deleteDoc(recipientRef)
      
      setEmailRecipients(prev => prev.filter(r => r.id !== recipientId))
    } catch (error) {
      console.error('Error removing email recipient:', error)
      throw error
    }
  }

  const updateEmailRecipient = async (recipientId: string, updates: Partial<EmailRecipient>) => {
    if (!user?.uid) throw new Error('User not authenticated')
    
    try {
      const recipientRef = doc(db, 'users', user.uid, 'email-recipients', recipientId)
      await updateDoc(recipientRef, updates)
      
      setEmailRecipients(prev => prev.map(r => 
        r.id === recipientId ? { ...r, ...updates } : r
      ))
    } catch (error) {
      console.error('Error updating email recipient:', error)
      throw error
    }
  }

  // Email Sending Functions
  const sendTestEmail = async (recipientIds: string[]): Promise<boolean> => {
    if (!settings.emailNotifications || !window.emailjs) {
      console.warn('Email notifications not enabled or EmailJS not configured')
      return false
    }

    const recipients = emailRecipients.filter(r => recipientIds.includes(r.id))
    if (recipients.length === 0) return false

    try {
      for (const recipient of recipients) {
        const recipientName = recipient.email.split('@')[0]
        const templateParams = {
          title: 'Prueba de Notificaciones',
          to_email: recipient.email,
          name: 'Sistema Mi Medicación',
          email: 'noreply@mimedicacion.app',
          to_name: recipientName,
          message: `¡Hola!\n\nEste es un correo de prueba del sistema Mi Medicación.\n\nSi recibiste este mensaje, las notificaciones están funcionando correctamente para tu cuenta.\n\nUsuario: ${user?.displayName || user?.email}\nFecha: ${new Date().toLocaleString('es-ES')}\n\n¡Gracias por usar Mi Medicación!`,
          nameSign: 'Equipo Mi Medicación',
          messageSign: 'Sistema de gestión de medicamentos'
        }

        await window.emailjs.send(
          'service_r7cangl',
          'template_ve5wc3p',
          templateParams
        )
      }
      return true
    } catch (error) {
      console.error('Error sending test email:', error)
      return false
    }
  }

  const sendLowStockEmail = async (medication: any, currentStock: number): Promise<boolean> => {
    if (!settings.emailNotifications || !settings.lowStockEmailAlerts || !window.emailjs) {
      return false
    }

    const recipients = emailRecipients.filter(r => r.lowStockAlerts)
    if (recipients.length === 0) return false

    try {
      for (const recipient of recipients) {
        const recipientName = recipient.email.split('@')[0]
        const templateParams = {
          title: 'Alerta de Stock Bajo',
          to_email: recipient.email,
          name: 'Sistema Mi Medicación',
          email: 'noreply@mimedicacion.app',
          to_name: recipientName,
          message: `⚠️ ALERTA DE STOCK BAJO ⚠️\n\nMedicamento: ${medication.name}\nStock actual: ${currentStock} unidades\nStock máximo: ${medication.maxStock} unidades\n\nEs recomendable reabastecer este medicamento pronto.\n\nUsuario: ${user?.displayName || user?.email}\nFecha: ${new Date().toLocaleString('es-ES')}`,
          nameSign: 'Sistema Mi Medicación',
          messageSign: '⚕️ Sistema de gestión de medicamentos'
        }

        await window.emailjs.send(
          'service_r7cangl',
          'template_ve5wc3p',
          templateParams
        )
      }
      return true
    } catch (error) {
      console.error('Error sending low stock email:', error)
      return false
    }
  }

  const sendMissedDoseEmail = async (medication: any, missedTime: string): Promise<boolean> => {
    if (!settings.emailNotifications || !settings.missedDoseEmailAlerts || !window.emailjs) {
      return false
    }

    const recipients = emailRecipients.filter(r => r.missedDoseAlerts)
    if (recipients.length === 0) return false

    try {
      for (const recipient of recipients) {
        const recipientName = recipient.email.split('@')[0]
        const templateParams = {
          title: 'Alerta de Dosis Perdida',
          to_email: recipient.email,
          name: 'Sistema Mi Medicación',
          email: 'noreply@mimedicacion.app',
          to_name: recipientName,
          message: `⚠️ ALERTA DE DOSIS PERDIDA ⚠️\n\nMedicamento: ${medication.name}\nHorario programado: ${missedTime}\nFecha: ${new Date().toLocaleDateString('es-ES')}\n\nNo se ha detectado la toma de este medicamento en el horario programado.\n\nUsuario: ${user?.displayName || user?.email}\nSistema: Mi Medicación\n\nSi ya tomó el medicamento, puede marcarlo como tomado en la aplicación.`,
          nameSign: 'Sistema Mi Medicación',
          messageSign: '⚕️ Sistema de gestión de medicamentos'
        }

        await window.emailjs.send(
          'service_r7cangl',
          'template_ve5wc3p',
          templateParams
        )
      }
      return true
    } catch (error) {
      console.error('Error sending missed dose email:', error)
      return false
    }
  }

  // Load email recipients when user changes
  useEffect(() => {
    if (user?.uid) {
      loadEmailRecipients()
    }
  }, [user?.uid])

  // Initialize EmailJS if available
  useEffect(() => {
    if (window.emailjs) {
      const emailjsKey = import.meta.env.VITE_EMAILJS_PUBLIC_KEY
      if (emailjsKey) {
        window.emailjs.init(emailjsKey)
      } else {
        console.warn('EmailJS public key not found in environment variables')
      }
    }
  }, [])

  // Cleanup reminders on unmount
  useEffect(() => {
    return () => {
      clearAllReminders()
    }
  }, [clearAllReminders])

  const value: NotificationContextType = {
    settings,
    permission,
    isSupported,
    scheduledRemindersCount: scheduledReminders.size,
    emailRecipients,
    updateSettings,
    requestPermission,
    sendNotification,
    scheduleReminder,
    cancelReminder,
    sendStockAlert,
    addEmailRecipient,
    removeEmailRecipient,
    updateEmailRecipient,
    sendTestEmail,
    sendLowStockEmail,
    sendMissedDoseEmail
  }

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  )
}

export const useNotifications = () => {
  const context = useContext(NotificationContext)
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider')
  }
  return context
} 